import React, { useState, useEffect, useContext } from "react"
import gql from "graphql-tag"
import { Mutation } from "react-apollo"
import { useForm } from "react-hook-form"
import validator from "validator"
import AppContext from "../../shared/AppContext"
import { BsFilePdf } from "react-icons/bs"
import { RiCloseCircleFill } from "react-icons/ri"
import Modal from "../Modal"

import "./freeReportRequest.scss"

const FREE_REPORT_REQUEST = gql`
  mutation FREE_REPORT_REQUEST(
    $email: String!
    $firstName: String!
    $lastName: String!
    $articleUrl: String!
    $reportTitle: String!
  ) {
    freeReportRequest(
      email: $email
      lastName: $lastName
      firstName: $firstName
      articleUrl: $articleUrl
      reportTitle: $reportTitle
    )
  }
`

const FreeReportRequestForm = (props) => {
  const { mutate, reportTitle, slug, isMobile } = props

  const [isSuccess, setSuccess] = useState(false)
  const [error, setError] = useState("")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { trackEvent } = useContext(AppContext)

  useEffect(() => {
    trackEvent("Free report request form component", {
      "Form Section": "Articles",
    })
  }, [])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    reportTitle,
    articleUrl: slug,
    firstName: "",
    lastName: "",
    email: "",
  })

  const onSubmit = async (data) => {
    const res = await mutate({
      variables: {
        ...data,
      },
    })

    if (
      res &&
      res.data &&
      res.data.freeReportRequest &&
      res.data.freeReportRequest === "success"
    ) {
      setSuccess(true)
      setError("")

      return
    } else {
      setSuccess(false)
      setError("An error occurred.")

      return
    }
  }

  const renderRequestForm = () => {
    return (
      <div>
        <p className="subheader">
          Fill out the form to receive a copy of &quot;{reportTitle}&quot; sent
          directly to your inbox.
        </p>
        <div className="request-form">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="form-field">
              <div className="input">
                <input
                  id="firstName"
                  placeholder="First Name"
                  data-cy="first-name-input"
                  name="firstName"
                  style={
                    errors.firstName
                      ? { border: "1px red solid" }
                      : { border: "1px solid #E2E8F0" }
                  }
                  ref={register({ required: true })}
                />
                {errors.firstName && (
                  <p className="error" data-cy="firstname-error-message">
                    First name is a required field.
                  </p>
                )}
              </div>
            </div>
            <div className="form-field">
              <div className="input">
                <input
                  id="lastName"
                  placeholder="Last Name"
                  data-cy="last-name-input"
                  style={
                    errors.lastName
                      ? { border: "1px red solid" }
                      : { border: "1px solid #E2E8F0" }
                  }
                  name="lastName"
                  ref={register({ required: true })}
                />
                {errors.lastName && (
                  <p className="error" data-cy="lastname-error-message">
                    Last name is a required field.
                  </p>
                )}
              </div>
            </div>
            <div className="form-field">
              <div className="input">
                <input
                  data-cy="email-input"
                  id="email"
                  placeholder="Email"
                  name="email"
                  style={
                    errors.email
                      ? { border: "1px red solid" }
                      : { border: "1px solid #E2E8F0" }
                  }
                  ref={register({
                    required: true,
                    validate: validator.isEmail,
                  })}
                />

                {errors.email && (
                  <p className="error" data-cy="email-error-message">
                    Please enter a valid email.
                  </p>
                )}
              </div>
            </div>

            <div className="form-field">
              <div className="input">
                <input
                  data-cy="article"
                  id="article"
                  hidden
                  name="articleUrl"
                  value={slug}
                  ref={register({ required: true })}
                />
              </div>
            </div>

            <div className="form-field">
              <div className="input">
                <input
                  data-cy="report-title"
                  id="report-title"
                  hidden
                  name="reportTitle"
                  value={reportTitle}
                  ref={register({ required: true })}
                />
              </div>
            </div>

            <button
              data-cy="free-report-submit"
              className="submit"
              type="submit"
            >
              Request free report
            </button>
          </form>
        </div>
      </div>
    )
  }

  const renderSuccess = () => {
    return (
      <div className="success" data-cy="success">
        <p className="message">
          Your request has been sucessfully submitted. Our team will promptly
          send you the report. Thank you!
        </p>
      </div>
    )
  }

  const renderComponent = () => {
    if (isMobile) {
      return (
        <>
          <div
            className="mobile-get-free-report-button"
            onClick={() => setIsModalOpen(true)}
          >
            <BsFilePdf size={"20"} /> Get Free Report
          </div>
          <Modal
            isOpen={isModalOpen}
            close={() => setIsModalOpen(false)}
            allowBackdropClose={true}
          >
            <div
              className="free-report-request-content"
              data-cy={"free-report-request-component"}
              style={{
                backgroundImage: `url("/static/images/free-trial-bg.png")`,
                backgroundSize: "cover",
              }}
            >
              <div className="header">
                <BsFilePdf size={"45"} />
                <p>Get Free Report</p>
              </div>

              <div className="close" onClick={() => setIsModalOpen(false)}>
                <RiCloseCircleFill />
              </div>

              {isSuccess ? renderSuccess() : renderRequestForm()}
              {!isSuccess && error && <p className="error">{error}</p>}
            </div>
          </Modal>
        </>
      )
    } else {
      return (
        <div
          className="free-report-request-content"
          data-cy={"free-report-request-component"}
          style={{
            backgroundImage: `url("/static/images/free-trial-bg.png")`,
            backgroundSize: "cover",
          }}
        >
          <div className="header">
            <BsFilePdf size={"45"} />
            <p>Get Free Report</p>
          </div>

          {isSuccess ? renderSuccess() : renderRequestForm()}
          {!isSuccess && error && <p className="error">{error}</p>}
        </div>
      )
    }
  }

  return renderComponent()
}

const FreeReportRequest = (props) => {
  return (
    <Mutation mutation={FREE_REPORT_REQUEST}>
      {(mutate) => {
        return <FreeReportRequestForm mutate={mutate} {...props} />
      }}
    </Mutation>
  )
}

export default FreeReportRequest
